/**
 * SubgraphModal — Modal wrapper for viewing explored subgraphs
 * 
 * Used in the chat transcript to show the graph exploration
 * for a specific query response. Fetches subgraph data on-demand
 * from the /graph/subgraph API endpoint.
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Box,
    Typography,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import FactCheckIcon from '@mui/icons-material/FactCheck';

import ForceGraphCanvas from './ForceGraphCanvas';
import type { GraphData, GraphNode, GraphLink } from './graph-types';
import { NODE_COLORS } from './graph-theme';
import { API_URL } from '../../lib/constants';
import { useCustomThemeContext } from '../../lib/contexts/ThemeContext';

interface SubgraphModalProps {
    /** Whether the modal is open */
    open: boolean;
    /** Callback to close the modal */
    onClose: () => void;
    /** Seed entity IDs to fetch subgraph for */
    seedIds: string[];
    /** Tenant ID for API calls */
    tenantId?: string;
    /** Optional title */
    title?: string;
    /** Number of hops to expand from seed nodes (default: 1) */
    hops?: number;
    /** Maximum number of nodes to return (default: 100) */
    limit?: number;
    /** IDs of chunks selected for the prompt */
    retrievedIds?: string[];
    /** IDs of chunks cited in the response */
    usedIds?: string[];
}

interface APIGraphNode {
    id: string;
    label: string;
    type: 'Document' | 'Chunk' | 'Entity';
    text?: string;
}

interface APIGraphEdge {
    source: string;
    target: string;
    type: string;
    predicate?: string;
}

/**
 * SubgraphModal Component
 */
export default function SubgraphModal({
    open,
    onClose,
    seedIds,
    tenantId = 'chip',
    title = 'Knowledge Graph Exploration',
    hops = 1,      // Default: 1 hop expansion (less explosion than 2)
    limit = 100,   // Default: 100 nodes max
    retrievedIds = [],
    usedIds = [],
}: SubgraphModalProps) {
    const { isDarkMode } = useCustomThemeContext();
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState<'searched' | 'retrieved' | 'used'>('searched');
    const [error, setError] = useState<string | null>(null);
    const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });

    // Fetch subgraph when modal opens
    useEffect(() => {
        const hasNodes = (seedIds && seedIds.length > 0) ||
                        (retrievedIds && retrievedIds.length > 0) ||
                        (usedIds && usedIds.length > 0);

        if (!open || !hasNodes) {
            return;
        }

        const fetchSubgraph = async () => {
            setLoading(true);
            setError(null);

            try {
                const allNodeIds = Array.from(new Set([
                    ...seedIds,
                    ...(retrievedIds || []),
                    ...(usedIds || [])
                ])).filter(Boolean);

                if (allNodeIds.length === 0) {
                    setLoading(false);
                    return;
                }

                const params = new URLSearchParams({
                    tenantId,
                    nodeIds: allNodeIds.join(','),
                    hops: String(hops),
                    limit: String(limit),
                });

                // Strip trailing slash from API_URL to avoid double slashes
                const baseUrl = (API_URL || '').replace(/\/+$/, '');
                const response = await fetch(`${baseUrl}/graph/subgraph?${params}`);

                if (!response.ok) {
                    throw new Error(`Failed to fetch subgraph: ${response.status}`);
                }

                const data = await response.json();

                // Transform API response to our format
                const nodes: GraphNode[] = (data.nodes || []).map((n: APIGraphNode) => ({
                    id: n.id,
                    label: n.label,
                    type: n.type,
                    text: n.text,
                }));

                const links: GraphLink[] = (data.edges || []).map((e: APIGraphEdge) => ({
                    source: e.source,
                    target: e.target,
                    type: e.type,
                    label: e.predicate || e.type,
                }));

                setGraphData({ nodes, links });
            } catch (err) {
                console.error('[SubgraphModal] Fetch error:', err);
                setError(err instanceof Error ? err.message : 'Failed to load graph');
            } finally {
                setLoading(false);
            }
        };

        fetchSubgraph();
    }, [open, seedIds, tenantId, hops, limit, retrievedIds, usedIds]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setGraphData({ nodes: [], links: [] });
            setError(null);
        }
    }, [open]);

    // Compute stats
    const stats = {
        entities: graphData.nodes.filter(n => n.type === 'Entity').length,
        chunks: graphData.nodes.filter(n => n.type === 'Chunk').length,
        documents: graphData.nodes.filter(n => n.type === 'Document').length,
        edges: graphData.links.length,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '80vh',
                    maxHeight: 800,
                    bgcolor: isDarkMode ? 'background.default' : 'background.paper',
                    backgroundImage: 'none',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScatterPlotIcon sx={{ color: NODE_COLORS.Entity }} />
                {title}
            </DialogTitle>

            <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: isDarkMode ? 'rgba(28, 37, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                            zIndex: 10,
                        }}
                    >
                        <CircularProgress size={48} />
                        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                            Loading graph exploration...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            p: 4,
                        }}
                    >
                        <Typography color="error" gutterBottom>
                            {error}
                        </Typography>
                        <Button variant="outlined" onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                )}

                {!loading && !error && (
                    <ForceGraphCanvas
                        graphData={graphData}
                        width="100%"
                        height="100%"
                        showControls
                        showLegend
                        viewMode={viewMode}
                        retrievedIds={retrievedIds}
                        usedIds={usedIds}
                    />
                )}
            </DialogContent>

            <DialogActions
                sx={(theme) => ({
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                    [theme.breakpoints.down('sm')]: {
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 2,
                    },
                })}
            >
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 0,
                        [theme.breakpoints.down('sm')]: {
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            width: '100%',
                        },
                    })}
                >
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, value) => value && setViewMode(value)}
                        size="small"
                        color="primary"
                        sx={(theme) => ({
                            '& .MuiToggleButton-root': { px: 2, gap: 1 },
                            [theme.breakpoints.down('sm')]: {
                                width: '100%',
                                '& .MuiToggleButton-root': {
                                    flex: 1,
                                    px: 0.5,
                                    gap: 0.5,
                                },
                            },
                        })}
                    >
                        <ToggleButton value="searched">
                            <SearchIcon fontSize="small" />
                            Searched
                        </ToggleButton>
                        <ToggleButton value="retrieved">
                            <InboxIcon fontSize="small" />
                            Retrieved
                        </ToggleButton>
                        <ToggleButton value="used">
                            <FactCheckIcon fontSize="small" />
                            Used
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Box
                        sx={(theme) => ({
                            height: 24,
                            width: 1,
                            bgcolor: 'divider',
                            flexShrink: 0,
                            [theme.breakpoints.down('sm')]: { display: 'none' },
                        })}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'center',
                            minWidth: 0,
                        }}
                    >
                        {graphData.nodes.length > 0 && (
                            <>
                                <Chip
                                    size="small"
                                    label={`${stats.entities} entities`}
                                    sx={{ bgcolor: NODE_COLORS.Entity, color: 'white' }}
                                />
                                {stats.chunks > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${stats.chunks} chunks`}
                                        sx={{ bgcolor: NODE_COLORS.Chunk, color: 'white' }}
                                    />
                                )}
                                {stats.documents > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${stats.documents} docs`}
                                        sx={{ bgcolor: NODE_COLORS.Document, color: 'white' }}
                                    />
                                )}
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {stats.edges} connections
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>

                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={(theme) => ({
                        flexShrink: 0,
                        justifyContent: 'center',
                        [theme.breakpoints.down('sm')]: {
                            alignSelf: 'center',
                            width: '100%',
                            transform: 'translateX(-1%)',
                        },
                    })}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
